import { useState, useMemo, useEffect } from "react";
import { 
  Search, Mail, ArrowLeft, StickyNote, User, MapPin, Copy, Check, 
  Phone, Crown, RefreshCw, AlertTriangle, Sparkles, Tag, Edit, Save, X,
  ShoppingBag, TrendingUp, Package, Users, AlertCircle
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { Order, DeliveryAddress } from "./OrderDetailModal";
import { formatUKDateTime } from "./OrderDetailModal";

interface Customer {
  id: string;
  email: string;
  name: string;
  phone: string | null;
  total_spent: number;
  orders_count: number;
  last_order_at: string | null;
  tags: string[];
  notes: string | null;
  created_at: string;
  updated_at: string;
  // Computed from orders
  addresses?: DeliveryAddress[];
  ordersList?: Order[];
  avgOrderValue?: number;
  possibleDuplicates?: string[];
}

interface CustomersPageProps {
  orders: Order[];
  onViewOrder: (order: Order) => void;
}

const tagConfig: Record<string, { label: string; color: string; icon: typeof Crown }> = {
  VIP: { label: "VIP", color: "bg-amber-900/30 text-amber-400 border-amber-900/50", icon: Crown },
  Returning: { label: "Returning", color: "bg-emerald-900/30 text-emerald-400 border-emerald-900/50", icon: RefreshCw },
  New: { label: "New", color: "bg-blue-900/30 text-blue-400 border-blue-900/50", icon: Sparkles },
  "At-risk": { label: "At-risk", color: "bg-red-900/30 text-red-400 border-red-900/50", icon: AlertTriangle },
};

export function CustomersPage({ orders, onViewOrder }: CustomersPageProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [segmentFilter, setSegmentFilter] = useState<string>("all");
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [editedNotes, setEditedNotes] = useState("");

  // Fetch customers from database
  useEffect(() => {
    fetchCustomers();
  }, []);

  // Sync customers from orders when orders change
  useEffect(() => {
    if (orders.length > 0) {
      syncCustomersFromOrders();
    }
  }, [orders]);

  const fetchCustomers = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("customers")
        .select("*")
        .order("total_spent", { ascending: false });

      if (error) throw error;
      setCustomers((data as Customer[]) || []);
    } catch (err) {
      console.error("Error fetching customers:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // Sync customers from orders (upsert) with email normalization
  const syncCustomersFromOrders = async () => {
    if (orders.length === 0) return;

    // Aggregate customer data from orders with normalized emails
    const customerMap = new Map<string, {
      email: string;
      name: string;
      phone: string | null;
      total_spent: number;
      orders_count: number;
      last_order_at: string;
    }>();

    orders.forEach(order => {
      const normalizedEmail = order.customer_email.toLowerCase().trim();
      const existing = customerMap.get(normalizedEmail);
      
      if (existing) {
        existing.total_spent += order.total;
        existing.orders_count += 1;
        if (new Date(order.created_at) > new Date(existing.last_order_at)) {
          existing.last_order_at = order.created_at;
          existing.name = order.customer_name;
          existing.phone = order.customer_phone || null;
        }
      } else {
        customerMap.set(normalizedEmail, {
          email: normalizedEmail,
          name: order.customer_name,
          phone: order.customer_phone || null,
          total_spent: order.total,
          orders_count: 1,
          last_order_at: order.created_at,
        });
      }
    });

    // Upsert customers
    for (const [email, data] of customerMap) {
      try {
        const { error } = await supabase
          .from("customers")
          .upsert(
            {
              email,
              name: data.name,
              phone: data.phone,
              total_spent: data.total_spent,
              orders_count: data.orders_count,
              last_order_at: data.last_order_at,
            },
            { onConflict: "email" }
          );

        if (error) console.error("Error upserting customer:", email, error);
      } catch (err) {
        console.error("Error syncing customer:", err);
      }
    }

    // Refresh customers list
    fetchCustomers();
  };

  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      toast.error("Failed to copy");
    }
  };

  const formatAddress = (addr: DeliveryAddress) => {
    return [addr.line1, addr.line2, addr.city, addr.county, addr.postcode, addr.country]
      .filter(Boolean)
      .join(", ");
  };

  const formatAddressMultiline = (addr: DeliveryAddress) => {
    return [addr.line1, addr.line2, addr.city, addr.county, addr.postcode, addr.country]
      .filter(Boolean)
      .join("\n");
  };

  // Detect possible duplicates (same name, different email)
  const findPossibleDuplicates = (customer: Customer, allCustomers: Customer[]): string[] => {
    const normalizedName = customer.name.toLowerCase().trim();
    return allCustomers
      .filter(c => 
        c.email !== customer.email && 
        c.name.toLowerCase().trim() === normalizedName
      )
      .map(c => c.email);
  };

  // Count duplicates
  const duplicatesCount = useMemo(() => {
    const nameMap = new Map<string, string[]>();
    customers.forEach(c => {
      const normalizedName = c.name.toLowerCase().trim();
      if (!nameMap.has(normalizedName)) {
        nameMap.set(normalizedName, []);
      }
      nameMap.get(normalizedName)!.push(c.email);
    });
    return Array.from(nameMap.values()).filter(emails => emails.length > 1).length;
  }, [customers]);

  // Enrich customers with order data
  const enrichedCustomers = useMemo(() => {
    return customers.map(customer => {
      const normalizedEmail = customer.email.toLowerCase().trim();
      const customerOrders = orders.filter(o => o.customer_email.toLowerCase().trim() === normalizedEmail);
      const addresses: DeliveryAddress[] = [];

      customerOrders.forEach(order => {
        const addrKey = formatAddress(order.delivery_address);
        if (!addresses.some(a => formatAddress(a) === addrKey)) {
          addresses.push(order.delivery_address);
        }
      });

      // Calculate avg order value
      const avgOrderValue = customer.orders_count > 0 
        ? customer.total_spent / customer.orders_count 
        : 0;

      // Auto-tag based on behavior
      const autoTags = [...customer.tags];
      const daysSinceLastOrder = customer.last_order_at
        ? Math.floor((Date.now() - new Date(customer.last_order_at).getTime()) / (1000 * 60 * 60 * 24))
        : 999;

      if (customer.total_spent >= 200 && !autoTags.includes("VIP")) {
        autoTags.push("VIP");
      }
      if (customer.orders_count > 1 && !autoTags.includes("Returning") && !autoTags.includes("New")) {
        autoTags.push("Returning");
      }
      if (customer.orders_count === 1 && daysSinceLastOrder < 30 && !autoTags.includes("New")) {
        autoTags.push("New");
      }
      if (daysSinceLastOrder > 60 && !autoTags.includes("At-risk")) {
        autoTags.push("At-risk");
      }

      // Find possible duplicates
      const possibleDuplicates = findPossibleDuplicates(customer, customers);

      return {
        ...customer,
        addresses,
        ordersList: customerOrders,
        tags: autoTags,
        avgOrderValue,
        possibleDuplicates,
      };
    });
  }, [customers, orders]);

  // Filter customers
  const filteredCustomers = useMemo(() => {
    let result = enrichedCustomers;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        c => c.name.toLowerCase().includes(query) || c.email.toLowerCase().includes(query)
      );
    }

    if (segmentFilter !== "all") {
      result = result.filter(c => c.tags.includes(segmentFilter));
    }

    return result;
  }, [enrichedCustomers, searchQuery, segmentFilter]);

  // Stats
  const customerStats = useMemo(() => {
    return {
      total: enrichedCustomers.length,
      vip: enrichedCustomers.filter(c => c.tags.includes("VIP")).length,
      returning: enrichedCustomers.filter(c => c.tags.includes("Returning")).length,
      new: enrichedCustomers.filter(c => c.tags.includes("New")).length,
      atRisk: enrichedCustomers.filter(c => c.tags.includes("At-risk")).length,
      totalRevenue: enrichedCustomers.reduce((sum, c) => sum + c.total_spent, 0),
      avgOrderValue: enrichedCustomers.length > 0 
        ? enrichedCustomers.reduce((sum, c) => sum + c.total_spent, 0) / 
          enrichedCustomers.reduce((sum, c) => sum + c.orders_count, 0) || 0
        : 0,
    };
  }, [enrichedCustomers]);

  // Update customer notes
  const handleSaveNotes = async () => {
    if (!selectedCustomer) return;

    try {
      const { error } = await supabase
        .from("customers")
        .update({ notes: editedNotes })
        .eq("id", selectedCustomer.id);

      if (error) throw error;

      setSelectedCustomer(prev => prev ? { ...prev, notes: editedNotes } : null);
      setIsEditingNotes(false);
      toast.success("Notes saved");
      fetchCustomers();
    } catch (err) {
      console.error("Error saving notes:", err);
      toast.error("Failed to save notes");
    }
  };

  // Update customer tags
  const handleToggleTag = async (tag: string) => {
    if (!selectedCustomer) return;

    const currentTags = selectedCustomer.tags.filter(t => !["VIP", "Returning", "New", "At-risk"].includes(t) || t === tag);
    const newTags = currentTags.includes(tag)
      ? currentTags.filter(t => t !== tag)
      : [...currentTags, tag];

    try {
      const { error } = await supabase
        .from("customers")
        .update({ tags: newTags })
        .eq("id", selectedCustomer.id);

      if (error) throw error;

      setSelectedCustomer(prev => prev ? { ...prev, tags: newTags } : null);
      toast.success("Tags updated");
      fetchCustomers();
    } catch (err) {
      console.error("Error updating tags:", err);
      toast.error("Failed to update tags");
    }
  };

  // Customer profile view
  if (selectedCustomer) {
    const sortedOrders = [...(selectedCustomer.ordersList || [])].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );

    // Get all products purchased
    const productsPurchased = useMemo(() => {
      const productMap = new Map<string, { name: string; quantity: number; revenue: number }>();
      sortedOrders.forEach(order => {
        order.items.forEach(item => {
          const key = item.name.toLowerCase();
          const existing = productMap.get(key);
          if (existing) {
            existing.quantity += item.quantity;
            existing.revenue += item.price * item.quantity;
          } else {
            productMap.set(key, {
              name: item.name,
              quantity: item.quantity,
              revenue: item.price * item.quantity,
            });
          }
        });
      });
      return Array.from(productMap.values()).sort((a, b) => b.revenue - a.revenue);
    }, [sortedOrders]);

    return (
      <div className="space-y-5">
        <Button
          variant="ghost"
          onClick={() => setSelectedCustomer(null)}
          className="text-slate-400 hover:text-slate-100 -ml-2 h-8"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Customers
        </Button>

        {/* Profile Header */}
        <Card className="bg-slate-800/50 border-slate-700 p-5">
          <div className="flex flex-col sm:flex-row items-start gap-4">
            <div className="w-16 h-16 bg-emerald-900/30 rounded-full flex items-center justify-center shrink-0">
              <span className="text-emerald-400 font-bold text-xl">
                {selectedCustomer.name.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-xl font-semibold text-slate-100">
                {selectedCustomer.name}
              </h2>
              <div className="flex items-center gap-2 mt-1">
                <p className="text-slate-400 text-sm flex items-center gap-1.5">
                  <Mail className="h-3.5 w-3.5" />
                  {selectedCustomer.email}
                </p>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => copyToClipboard(selectedCustomer.email, "email")}
                >
                  {copiedField === "email" ? (
                    <Check className="h-3 w-3 text-emerald-400" />
                  ) : (
                    <Copy className="h-3 w-3 text-slate-400" />
                  )}
                </Button>
              </div>
              {selectedCustomer.phone && (
                <p className="text-slate-400 text-sm mt-0.5 flex items-center gap-1.5">
                  <Phone className="h-3.5 w-3.5" />
                  {selectedCustomer.phone}
                </p>
              )}

              {/* Duplicate Warning */}
              {selectedCustomer.possibleDuplicates && selectedCustomer.possibleDuplicates.length > 0 && (
                <div className="mt-2 p-2 bg-amber-900/20 border border-amber-900/30 rounded-lg">
                  <p className="text-amber-400 text-xs flex items-center gap-1.5">
                    <AlertCircle className="h-3.5 w-3.5" />
                    Possible duplicate: {selectedCustomer.possibleDuplicates.join(", ")}
                  </p>
                </div>
              )}

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mt-3">
                {selectedCustomer.tags.map(tag => {
                  const config = tagConfig[tag];
                  if (!config) return null;
                  const Icon = config.icon;
                  return (
                    <Badge 
                      key={tag} 
                      className={`cursor-pointer ${config.color}`}
                      onClick={() => handleToggleTag(tag)}
                    >
                      <Icon className="w-3 h-3 mr-1" />
                      {config.label}
                    </Badge>
                  );
                })}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 text-slate-500 hover:text-slate-300"
                  onClick={() => handleToggleTag("VIP")}
                >
                  <Tag className="w-3 h-3 mr-1" />
                  Manage Tags
                </Button>
              </div>
            </div>
          </div>
        </Card>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card className="bg-slate-800/30 border-slate-700 p-4">
            <div className="text-2xl font-bold text-emerald-400">
              £{selectedCustomer.total_spent.toFixed(2)}
            </div>
            <div className="text-slate-500 text-xs">Total Spent</div>
          </Card>
          <Card className="bg-slate-800/30 border-slate-700 p-4">
            <div className="text-2xl font-bold text-slate-100">
              {selectedCustomer.orders_count}
            </div>
            <div className="text-slate-500 text-xs">Orders</div>
          </Card>
          <Card className="bg-slate-800/30 border-slate-700 p-4">
            <div className="text-2xl font-bold text-slate-100">
              £{(selectedCustomer.avgOrderValue || 0).toFixed(2)}
            </div>
            <div className="text-slate-500 text-xs">Avg Order Value</div>
          </Card>
          <Card className="bg-slate-800/30 border-slate-700 p-4">
            <div className="text-lg font-bold text-slate-100">
              {selectedCustomer.last_order_at 
                ? new Date(selectedCustomer.last_order_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })
                : "Never"}
            </div>
            <div className="text-slate-500 text-xs">Last Order</div>
          </Card>
        </div>

        {/* Products Purchased */}
        {productsPurchased.length > 0 && (
          <Card className="bg-slate-800/30 border-slate-700 p-4">
            <h3 className="text-sm font-semibold text-slate-200 mb-3 flex items-center gap-2">
              <Package className="h-4 w-4 text-emerald-400" />
              Products Purchased
            </h3>
            <div className="space-y-2">
              {productsPurchased.slice(0, 5).map((product, idx) => (
                <div key={idx} className="flex items-center justify-between py-1.5">
                  <div className="flex items-center gap-2">
                    <span className="text-slate-200 text-sm">{product.name}</span>
                    <Badge variant="secondary" className="bg-slate-700 text-slate-300 text-xs">
                      ×{product.quantity}
                    </Badge>
                  </div>
                  <span className="text-emerald-400 font-medium text-sm">£{product.revenue.toFixed(2)}</span>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Notes Section */}
        <Card className="bg-slate-800/30 border-slate-700 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-200 flex items-center gap-2">
              <StickyNote className="h-4 w-4" />
              Internal Notes
            </h3>
            {isEditingNotes ? (
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={() => setIsEditingNotes(false)} className="h-7">
                  <X className="w-3 h-3 mr-1" />
                  Cancel
                </Button>
                <Button size="sm" onClick={handleSaveNotes} className="h-7 bg-emerald-600 hover:bg-emerald-700">
                  <Save className="w-3 h-3 mr-1" />
                  Save
                </Button>
              </div>
            ) : (
              <Button 
                size="sm" 
                variant="ghost" 
                onClick={() => {
                  setEditedNotes(selectedCustomer.notes || "");
                  setIsEditingNotes(true);
                }}
                className="h-7 text-slate-400"
              >
                <Edit className="w-3 h-3 mr-1" />
                Edit
              </Button>
            )}
          </div>
          {isEditingNotes ? (
            <Textarea
              value={editedNotes}
              onChange={(e) => setEditedNotes(e.target.value)}
              placeholder="Add notes about this customer..."
              className="bg-slate-900 border-slate-700 text-slate-100"
              rows={4}
            />
          ) : (
            <p className="text-slate-400 text-sm whitespace-pre-wrap">
              {selectedCustomer.notes || "No notes yet. Click Edit to add notes about this customer."}
            </p>
          )}
        </Card>

        {/* Addresses */}
        {selectedCustomer.addresses && selectedCustomer.addresses.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-slate-200 mb-3 flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              Shipping Addresses ({selectedCustomer.addresses.length})
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {selectedCustomer.addresses.map((addr, idx) => (
                <Card key={idx} className="bg-slate-800/30 border-slate-700 p-3">
                  <div className="flex items-start justify-between">
                    <div className="text-sm text-slate-300 leading-relaxed">
                      {formatAddress(addr)}
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 shrink-0"
                      onClick={() => copyToClipboard(`${selectedCustomer.name}\n${formatAddressMultiline(addr)}`, `addr-${idx}`)}
                    >
                      {copiedField === `addr-${idx}` ? (
                        <Check className="h-3 w-3 text-emerald-400" />
                      ) : (
                        <Copy className="h-3 w-3 text-slate-400" />
                      )}
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Order History */}
        <div>
          <h3 className="text-sm font-semibold text-slate-200 mb-3 flex items-center gap-2">
            <ShoppingBag className="h-4 w-4" />
            Order History ({sortedOrders.length})
          </h3>
          {sortedOrders.length === 0 ? (
            <Card className="bg-slate-900/70 border-slate-700/60 p-8 text-center">
              <p className="text-slate-400">No orders found for this customer</p>
            </Card>
          ) : (
            <Card className="bg-slate-900/70 border-slate-700/60 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700/60">
                    <TableHead className="text-slate-300 text-xs">Order</TableHead>
                    <TableHead className="text-slate-300 text-xs">Date</TableHead>
                    <TableHead className="text-slate-300 text-xs hidden sm:table-cell">Products</TableHead>
                    <TableHead className="text-slate-300 text-xs">Total</TableHead>
                    <TableHead className="text-slate-300 text-xs">Status</TableHead>
                    <TableHead className="text-slate-300 text-xs"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedOrders.map((order) => (
                    <TableRow key={order.id} className="border-slate-700/60 hover:bg-slate-800/50">
                      <TableCell className="font-mono text-xs text-slate-200 py-2">
                        {order.order_number}
                      </TableCell>
                      <TableCell className="text-slate-300 text-xs py-2">
                        {formatUKDateTime(order.created_at)}
                      </TableCell>
                      <TableCell className="text-slate-400 text-xs hidden sm:table-cell py-2 max-w-[200px]">
                        <div className="truncate">
                          {order.items.map(i => i.name).join(", ")}
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-100 font-semibold text-sm py-2">
                        £{order.total.toFixed(2)}
                      </TableCell>
                      <TableCell className="py-2">
                        <Badge
                          className={`text-[10px] ${
                            order.fulfilled
                              ? "bg-emerald-900/30 text-emerald-400 border-emerald-900/50"
                              : "bg-amber-900/30 text-amber-400 border-amber-900/50"
                          }`}
                        >
                          {order.fulfilled ? "Fulfilled" : "Unfulfilled"}
                        </Badge>
                      </TableCell>
                      <TableCell className="py-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onViewOrder(order)}
                          className="text-emerald-400 hover:text-emerald-300 h-7 text-xs"
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </div>
      </div>
    );
  }

  // Empty state - but only show if no orders exist
  if (!isLoading && enrichedCustomers.length === 0 && orders.length === 0) {
    return (
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-slate-100">Customer Directory</h2>
            <p className="text-slate-400 text-sm mt-0.5">Manage your customer relationships</p>
          </div>
        </div>
        <Card className="bg-slate-900/70 border-slate-700/60 p-12 text-center">
          <div className="w-20 h-20 bg-emerald-900/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <User className="w-10 h-10 text-emerald-400" />
          </div>
          <h3 className="text-xl font-semibold text-slate-100 mb-2">No customers yet</h3>
          <p className="text-slate-400 mb-6 max-w-md mx-auto">
            Your customer directory will populate automatically as orders come in. Each unique email creates a customer profile.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-slate-100">Customer Directory</h2>
          <p className="text-slate-400 text-sm mt-0.5">
            {customerStats.total} customers • £{customerStats.totalRevenue.toFixed(2)} lifetime value
          </p>
        </div>
        {duplicatesCount > 0 && (
          <Badge className="bg-amber-900/30 text-amber-400 border-amber-900/50">
            <AlertCircle className="w-3 h-3 mr-1" />
            {duplicatesCount} possible duplicate{duplicatesCount !== 1 ? "s" : ""} detected
          </Badge>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <Input
            placeholder="Search customers by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-slate-900/70 border-slate-700 text-slate-100 placeholder:text-slate-500 h-9"
          />
        </div>

        <Select value={segmentFilter} onValueChange={setSegmentFilter}>
          <SelectTrigger className="w-[140px] bg-slate-900/70 border-slate-700 text-slate-300 h-9">
            <SelectValue placeholder="Segment" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Customers</SelectItem>
            <SelectItem value="VIP">VIP</SelectItem>
            <SelectItem value="Returning">Returning</SelectItem>
            <SelectItem value="New">New</SelectItem>
            <SelectItem value="At-risk">At-risk</SelectItem>
          </SelectContent>
        </Select>

        <Badge variant="outline" className="border-slate-600 text-slate-300 px-3 py-1.5 h-9 flex items-center">
          {filteredCustomers.length} result{filteredCustomers.length !== 1 ? "s" : ""}
        </Badge>
      </div>

      {/* Segment Quick Filters */}
      <div className="flex gap-2 flex-wrap">
        {Object.entries(tagConfig).map(([tag, config]) => {
          const count = enrichedCustomers.filter(c => c.tags.includes(tag)).length;
          const Icon = config.icon;
          return (
            <Badge
              key={tag}
              className={`cursor-pointer transition-all ${
                segmentFilter === tag ? config.color : "bg-slate-800 text-slate-400 border-slate-700 hover:bg-slate-700"
              }`}
              onClick={() => setSegmentFilter(segmentFilter === tag ? "all" : tag)}
            >
              <Icon className="w-3 h-3 mr-1" />
              {config.label} ({count})
            </Badge>
          );
        })}
      </div>

      {/* Customers Table */}
      {isLoading ? (
        <Card className="bg-slate-900/70 border-slate-700/60 p-12 text-center">
          <p className="text-slate-400">Loading customers...</p>
        </Card>
      ) : filteredCustomers.length === 0 ? (
        <Card className="bg-slate-900/70 border-slate-700/60 p-12 text-center">
          <User className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-400">No customers match your filters</p>
        </Card>
      ) : (
        <Card className="bg-slate-900/70 border-slate-700/60 overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700/60 hover:bg-slate-800/50">
                  <TableHead className="text-slate-300 text-xs">Customer</TableHead>
                  <TableHead className="text-slate-300 text-xs hidden sm:table-cell">Email</TableHead>
                  <TableHead className="text-slate-300 text-xs text-right">Orders</TableHead>
                  <TableHead className="text-slate-300 text-xs text-right">Total Spent</TableHead>
                  <TableHead className="text-slate-300 text-xs hidden md:table-cell">Tags</TableHead>
                  <TableHead className="text-slate-300 text-xs hidden lg:table-cell">Last Order</TableHead>
                  <TableHead className="text-slate-300 text-xs"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCustomers.map((customer) => (
                  <TableRow
                    key={customer.id}
                    className="border-slate-700/60 hover:bg-slate-800/50 cursor-pointer"
                    onClick={() => setSelectedCustomer(customer)}
                  >
                    <TableCell className="py-2">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-emerald-900/30 rounded-full flex items-center justify-center relative">
                          <span className="text-emerald-400 font-semibold text-xs">
                            {customer.name.charAt(0).toUpperCase()}
                          </span>
                          {customer.possibleDuplicates && customer.possibleDuplicates.length > 0 && (
                            <div className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-amber-500 rounded-full" />
                          )}
                        </div>
                        <div>
                          <div className="text-slate-100 font-medium text-sm">{customer.name}</div>
                          <div className="text-[10px] text-slate-500 sm:hidden">{customer.email}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-300 text-xs hidden sm:table-cell py-2">
                      {customer.email}
                    </TableCell>
                    <TableCell className="py-2 text-right">
                      <Badge variant="secondary" className="bg-slate-700 text-slate-200 text-xs">
                        {customer.orders_count}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-emerald-400 font-semibold text-sm py-2 text-right">
                      £{customer.total_spent.toFixed(2)}
                    </TableCell>
                    <TableCell className="py-2 hidden md:table-cell">
                      <div className="flex gap-1">
                        {customer.tags.slice(0, 2).map(tag => {
                          const config = tagConfig[tag];
                          if (!config) return null;
                          return (
                            <Badge key={tag} className={`text-[9px] ${config.color}`}>
                              {config.label}
                            </Badge>
                          );
                        })}
                        {customer.tags.length > 2 && (
                          <Badge className="text-[9px] bg-slate-700 text-slate-400">
                            +{customer.tags.length - 2}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-400 text-xs hidden lg:table-cell py-2">
                      {customer.last_order_at ? formatUKDateTime(customer.last_order_at) : "Never"}
                    </TableCell>
                    <TableCell className="py-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-slate-400 hover:text-slate-100 h-7 text-xs"
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
}
